import {
  useBackgroundQuery,
  useQuery,
  useReadQuery,
} from "@apollo/client/react";
import { useMemo } from "react";

import type { RecentBookingsQueryQuery } from "../../gql/graphql";
import { FeatureFlag } from "../../services/flags/enum";
import { getFeatureIsEnabled } from "../../services/flags/featureFlags";
import { recentBookingsQuery } from "./recentBookingsQuery";
import type { BackgroundQueryRef } from "./types/BackgroundQueryRef";

export const useRecentBookingsBackgroundQuery =
  (): BackgroundQueryRef<RecentBookingsQueryQuery> => {
    const [queryRef] = useBackgroundQuery(recentBookingsQuery, {
      fetchPolicy: "cache-first",
    });

    return queryRef;
  };

export const useRecentBookings = () => {
  const { data, loading, error } = useQuery(recentBookingsQuery, {
    fetchPolicy: "cache-first",
  });

  return {
    bookings: data?.viewer.bookings.recent ?? [],
    loading,
    error,
  };
};

const MAX_RECENT_GOODS_TYPES = 5;

export const useRecentGoodsTypeCodes = (
  queryRef: BackgroundQueryRef<RecentBookingsQueryQuery>,
): string[] => {
  const { data } = useReadQuery(queryRef);
  const bookings = data?.viewer.bookings.recent;

  return useMemo(() => {
    const codes = new Set<string>();
    $outer: for (const booking of bookings ?? []) {
      if (!booking) {
        continue;
      }
      for (const goods of booking.goods) {
        if (goods.goodsType?.code) {
          codes.add(goods.goodsType.code);
          if (codes.size >= MAX_RECENT_GOODS_TYPES) {
            break $outer;
          }
        }
      }
    }
    return [...codes];
  }, [bookings]);
};

export const useRecentVehicleTypeCodes = (): string[] => {
  const { bookings } = useRecentBookings();

  return useMemo(() => {
    if (!getFeatureIsEnabled(FeatureFlag.RecentRoutesAndVehicleTypes)) {
      return [];
    }
    const codes = new Set<string>();
    for (const booking of bookings) {
      if (!booking) {
        continue;
      }
      if (booking.vehicleType?.code) {
        codes.add(booking.vehicleType.code);
      }
    }
    return [...codes];
  }, [bookings]);
};

export const useRecentRouteCodes = (): string[] => {
  const { bookings } = useRecentBookings();

  return useMemo(() => {
    if (!getFeatureIsEnabled(FeatureFlag.RecentRoutesAndVehicleTypes)) {
      return [];
    }
    const codes = new Set<string>();
    for (const booking of bookings) {
      if (!booking) {
        continue;
      }
      if (booking.sailing?.route?.code) {
        codes.add(booking.sailing.route.code);
      }
    }
    return [...codes];
  }, [bookings]);
};
